import { getSalesforceCases } from 'actions/salesforce'
import DriveSidebar from 'components/layout/DriveSidebar'
import Panel, { PanelTitle } from 'components/layout/Panel'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import { LockReason } from 'types/profile'
import { useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { Button, Container, Faq, Icon, RoadieApiResponse, Spacer, Text, Time } from 'roadie-ui'
import Page from 'rui/widgets/Page'
import { getLastUpdatedOpenCase, SalesforceCase } from 'util/salesforce'
import { useLocalization, LocalizedText } from 'localization'

const canStillSubmitAppeal = (lockedAt: Time, numberOfDays: number) => {
  return lockedAt.date.getTime() > Date.now() - numberOfDays * 1000 * 60 * 60 * 24
}

type AccountStatusIconProps = {
  accountStatus: string
}

const AccountStatusIcon: React.FC<AccountStatusIconProps> = ({ accountStatus: accountStatus }) => {
  switch (accountStatus) {
    case 'acceptable':
      return <Icon.CheckmarkCircle $color="success-base" $width="2.4rem" $height="2.4rem" />
    case 'coaching':
      return <Icon.AlertFilled $color="alert-base" $width="2.4rem" $height="2.4rem" />
    case 'permanent':
      return <Icon.AlertFilled $color="error-base" $width="2.4rem" $height="2.4rem" />
    default:
      return null
  }
}

export const AccountStatusPanel: React.FC = () => {
  return (
    <Panel left noPadding>
      <Container.Grid $padding="2.4rem" $gridTemplateColumns="5rem 1fr max-content" $alignItems="center">
        <Container.Grid $textAlign="left">
          <Text.Caption>
            <LocalizedText translationKey="dweb_dashboard_account_status" />
          </Text.Caption>
          <Spacer $height="0.4rem" />
          <Container.Grid
            $gridTemplateColumns="2.4rem auto"
            $gridGap=".8rem"
            $justifyContent="center"
            $alignItems="center"
          >
            <AccountStatusIcon accountStatus={'permanent'} />
            <Text.Subtitle2 $fontWeight="bold" $color="neutral-black" $whiteSpace="nowrap">
              <LocalizedText translationKey="dweb_dashboard_account_status_deactivated" />
            </Text.Subtitle2>
          </Container.Grid>
        </Container.Grid>
        <Container.Position />
        <Button.Link.Tertiary type="button" id="account-status-link" to={`/account/account-status`}>
          <Container.Grid $justifyItems="end">
            <Icon.ChevronRight $color="neutral-base" $width="2.4rem" $height="2.4rem" />
          </Container.Grid>
        </Button.Link.Tertiary>
      </Container.Grid>
    </Panel>
  )
}

const AccountStatusPage: React.FC = () => {
  const { t } = useLocalization()
  const lockReason = useSelector<any, LockReason>((state) => state.lockReason)
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const [appealSummary, setAppealSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getSalesforceCases().then((data: RoadieApiResponse) => {
      const mostRecentAppeal: SalesforceCase | null = getLastUpdatedOpenCase(data, 'appeal')
      setAppealSummary(mostRecentAppeal || null)
      setLoading(false)
    })
  }, [])

  // Redirect to /dashboard if driver is not permanently locked and ends up on this route
  if (lockReason?.reason !== 'permanent') {
    return <Redirect to={{ pathname: '/dashboard' }} />
  }

  const stillCanSubmitAppeal = canStillSubmitAppeal(Time.fromDate(new Date(lockReason?.locked_at)), 90)

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_dashboard_account_status')}</title>
      </Helmet>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_my_profile'), linkTo: '/dashboard' },
            { label: t('dweb_dashboard_account_status') },
          ]}
        />
      )}
      <Panel noPadding>
        <Container.Base $padding="2.4rem" $textAlign="left">
          <Text.Body1 $fontSize="2.4rem" $color="neutral-black" $fontWeight="500">
            <LocalizedText translationKey="dweb_dashboard_account_status_account_permanently_deactivated" />
          </Text.Body1>
          <Spacer $height="1.6rem" />
          <Text.Body1>
            <LocalizedText translationKey="dweb_dashboard_account_status_may_appeal_decision_by_completing_appeals_form_linked" />
          </Text.Body1>
        </Container.Base>
        {!loading &&
          (appealSummary === null ? (
            stillCanSubmitAppeal ? (
              <Button.Link.Tertiary type="button" id="submit-appeal-button" to={`appeal`} $width="100%">
                <Container.Grid
                  $padding="1.6rem 2.4rem"
                  $gridTemplateColumns="1fr max-content"
                  $alignItems="center"
                  $borderWidth="1px 0 0 0"
                  $borderColor="neutral-light"
                  $borderStyle="solid"
                >
                  <Container.GridItem $textAlign="left">
                    <LocalizedText translationKey="dweb_dashboard_account_status_submit_appeal" />
                  </Container.GridItem>
                  <Container.GridItem $alignSelf="right">
                    <Icon.ChevronRight $color="neutral-base" $width="2.4rem" $height="2.4rem" />
                  </Container.GridItem>
                </Container.Grid>
              </Button.Link.Tertiary>
            ) : (
              <Container.Grid
                $padding="1.6rem 2.4rem"
                $gridTemplateColumns="1fr max-content"
                $alignItems="center"
                $borderWidth="1px 0 0 0"
                $borderColor="neutral-light"
                $borderStyle="solid"
              >
                <Container.GridItem $textAlign="left">
                  <Button.Tertiary disabled={true}>
                    <LocalizedText translationKey="dweb_dashboard_account_status_submit_appeal" />
                  </Button.Tertiary>
                  <Spacer $height="0.4rem" />
                  <Text.Caption>
                    <LocalizedText translationKey="dweb_dashboard_account_status_window_submit_appeal_has_closed" />
                  </Text.Caption>
                </Container.GridItem>
                <Container.GridItem $alignSelf="right">
                  <Icon.ChevronRight $color="neutral-base" $width="2.4rem" $height="2.4rem" />
                </Container.GridItem>
              </Container.Grid>
            )
          ) : (
            <Button.Link.Tertiary type="button" id="submit-appeal-button" to={`appeal-status`} $width="100%">
              <Container.Grid
                $padding="1.6rem 2.4rem"
                $gridTemplateColumns="1fr max-content"
                $alignItems="center"
                $borderWidth="1px 0 0 0"
                $borderColor="neutral-light"
                $borderStyle="solid"
              >
                <Container.GridItem $textAlign="left">
                  <LocalizedText translationKey="dweb_dashboard_account_status_view_appeal_status" />
                </Container.GridItem>
                <Container.GridItem $alignSelf="right">
                  <Icon.ChevronRight $color="neutral-base" $width="2.4rem" $height="2.4rem" />
                </Container.GridItem>
              </Container.Grid>
            </Button.Link.Tertiary>
          ))}
      </Panel>
      <Spacer $height="1.6rem" />
      <PanelTitle>
        <LocalizedText translationKey="dweb_dashboard_account_status_faqs" />
      </PanelTitle>
      <Panel noPadding>
        <Container.Base $padding="1.6rem 2.4rem">
          <Faq title={t('dweb_dashboard_account_status_how_does_affect_my_account')}>
            <LocalizedText translationKey="dweb_dashboard_account_status_wont_able_view_access_any_gigs_instant_pay" />
          </Faq>
          <Spacer $height="2.4rem" />
          <Faq title={t('dweb_dashboard_account_status_what_happens_my_balance')}>
            <LocalizedText translationKey="dweb_dashboard_account_status_balance_will_automatically_paid_out_wednesday_as_part" />
          </Faq>
        </Container.Base>
        <Container.Base $padding="2.4rem">
          <Button.A.Tertiary
            href={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Driver-Gig-logistics-Unlocking-your-account`}
          >
            <LocalizedText translationKey="dweb_dashboard_account_status_view_more_info" />
          </Button.A.Tertiary>
        </Container.Base>
      </Panel>
    </Page>
  )
}

export default AccountStatusPage
